import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 — direct gtag.js injection (no GTM intermediary).
 *
 * Why direct gtag.js?
 *   We tried wrapping GA4 inside a Google Tag Manager container
 *   (GTM-…) but the project doesn't yet need GTM's "add more tags
 *   without redeploying" flexibility. A single GA4 property goes
 *   straight to the browser with one script tag — fewer moving
 *   parts, no dashboard configuration step, no risk of double-
 *   counting if both GTM and gtag.js are accidentally loaded.
 *
 * What this component does:
 *   1. Reads VITE_GA_MEASUREMENT_ID. Empty → component is a no-op
 *      so dev sessions never ship analytics or hit Google's CDN.
 *   2. Injects gtag.js exactly the way the Google Analytics console
 *      snippet does: window.dataLayer + window.gtag, then a
 *      {@code <script async>} pointing at googletagmanager.com.
 *   3. Sends a {@code config} call with {@code send_page_view: false}
 *      so the initial automatic page_view doesn't double up with
 *      the per-route push we control below.
 *   4. On every React Router navigation, fires a manual page_view
 *      event scoped to {@code location.pathname} only — NEVER the
 *      query string, so OAuth tokens / verify codes / ?next= values
 *      never leak to Google.
 *
 * Mount this once at the top of the React tree, inside the
 * BrowserRouter (useLocation must resolve). It renders nothing.
 */
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function injectGtag(measurementId: string) {
  if (window.gtag) return; // already injected

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  // send_page_view:false ⇒ Google won't auto-fire the first page_view;
  // we send our own below so the path/title match the actual SPA route
  // rather than whatever URL the user landed on (which could include
  // query secrets).
  window.gtag('config', measurementId, { send_page_view: false });
}

export function GoogleAnalytics() {
  const measurementId = (
    import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined
  )?.trim();
  const location = useLocation();
  const injected = useRef(false);

  // One-shot injection.
  useEffect(() => {
    if (!measurementId) return;
    if (injected.current) return;
    injectGtag(measurementId);
    injected.current = true;
  }, [measurementId]);

  // Per-route page_view event. We send only `pathname` (never the
  // query string) for privacy — see comment above.
  useEffect(() => {
    if (!measurementId) return;
    if (!window.gtag) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname,
      page_location: window.location.origin + location.pathname,
      page_title: document.title,
    });
  }, [measurementId, location.pathname]);

  return null;
}
